import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { GraduationCap, Eye, EyeOff, Package, CreditCard, ArrowLeft, Check, Building2, Briefcase } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  
  // Get package info from URL params
  const selectedPackage = {
    id: searchParams.get('package_id'),
    name: searchParams.get('package_name'),
    type: searchParams.get('package_type'),
    price: searchParams.get('price'),
    credits: searchParams.get('credits')
  };
  
  const hasSelectedPackage = selectedPackage.id && selectedPackage.name;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: selectedPackage.type || 'student',
    phone: '',
    business_name: '',  // For agents
    organization_name: ''  // For organizations
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Update role when package type changes
  useEffect(() => {
    if (selectedPackage.type) {
      setFormData(prev => ({ ...prev, role: selectedPackage.type }));
    }
  }, [selectedPackage.type]);

  // Get role display name
  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'student': return 'Student';
      case 'agent': return 'Education Agent';
      case 'organization': return 'Organization';
      default: return role;
    }
  };

  // Get role icon
  const getRoleIcon = (role) => {
    switch (role) {
      case 'agent': return Briefcase;
      case 'organization': return Building2;
      default: return GraduationCap;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Validate required fields based on role
    if (formData.role === 'agent' && !formData.business_name.trim()) {
      toast.error('Business name is required for agents');
      return;
    }

    if (formData.role === 'organization' && !formData.organization_name.trim()) {
      toast.error('Organization name is required');
      return;
    }
    
    setLoading(true);
    
    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        phone: formData.phone || null,
        selected_package_id: selectedPackage.id || null
      };

      // Add role-specific fields
      if (formData.role === 'agent') {
        userData.business_name = formData.business_name;
      }
      if (formData.role === 'organization') {
        userData.organization_name = formData.organization_name;
      }

      const user = await register(userData);
      
      toast.success('Account created successfully!');
      
      // Redirect based on role
      switch (user.role) {
        case 'super_admin':
          navigate('/admin');
          break;
        case 'agent':
          navigate('/agent');
          break;
        case 'organization':
          navigate('/organization');
          break;
        default:
          navigate('/student');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const RoleIcon = getRoleIcon(formData.role);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-muted/30">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg bg-lime-500 flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="VisaInterviews.ai" className="w-7 h-7 object-contain" />
          </div>
          <span className="font-heading font-bold text-2xl">
            Visa<span className="text-lime-500">interviews</span>.ai
          </span>
        </Link>

        {/* Selected Package Display */}
        {hasSelectedPackage && (
          <Card className="mb-6 border-lime-500/50 bg-lime-50/50 dark:bg-lime-950/20">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-lime-500/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-lime-600 dark:text-lime-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Selected Plan</p>
                    <p className="font-heading font-semibold text-lg">{selectedPackage.name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <CreditCard className="w-3 h-3" />
                        £{selectedPackage.price}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {selectedPackage.credits} credit{selectedPackage.credits !== '1' ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
                <Badge className="bg-lime-500 text-white">
                  {getRoleDisplayName(selectedPackage.type)}
                </Badge>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="mt-3 text-xs text-muted-foreground hover:text-foreground gap-1"
                onClick={() => navigate('/pricing')}
              >
                <ArrowLeft className="w-3 h-3" />
                Change plan
              </Button>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl">Create Account</CardTitle>
            <CardDescription>
              {hasSelectedPackage 
                ? `Complete your registration for the ${selectedPackage.name} plan`
                : 'Get started with your interview preparation'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Account Type - Show dropdown or read-only based on package selection */}
              <div className="space-y-2">
                <Label htmlFor="role">Account Type</Label>
                {hasSelectedPackage ? (
                  <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-muted/50">
                    <RoleIcon className="w-4 h-4 text-lime-500" />
                    <span className="text-sm">{getRoleDisplayName(formData.role)}</span>
                    <span className="text-xs text-muted-foreground ml-auto">(based on selected plan)</span>
                  </div>
                ) : (
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value, business_name: '', organization_name: '' })}
                  >
                    <SelectTrigger data-testid="register-role">
                      <SelectValue placeholder="Select account type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">
                        <div className="flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-blue-500" />
                          Student
                        </div>
                      </SelectItem>
                      <SelectItem value="agent">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-purple-500" />
                          Education Agent
                        </div>
                      </SelectItem>
                      <SelectItem value="organization">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-orange-500" />
                          Organization
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Organization Name - Only for organizations */}
              {formData.role === 'organization' && (
                <div className="space-y-2">
                  <Label htmlFor="organization_name">Organization Name *</Label>
                  <Input
                    id="organization_name"
                    placeholder="ABC University / XYZ College"
                    value={formData.organization_name}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    required
                    data-testid="register-organization-name"
                  />
                  <p className="text-xs text-muted-foreground">The official name of your organization</p>
                </div>
              )}

              {/* Business Name - Only for agents */}
              {formData.role === 'agent' && (
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    placeholder="Immigration Consultants Ltd"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    required
                    data-testid="register-business-name"
                  />
                  <p className="text-xs text-muted-foreground">Your company or business name</p>
                </div>
              )}

              {/* Name field - label changes based on role */}
              <div className="space-y-2">
                <Label htmlFor="name">
                  {formData.role === 'student' ? 'Full Name *' : 'Representative Name *'}
                </Label>
                <Input
                  id="name"
                  placeholder={formData.role === 'student' ? 'John Doe' : 'Contact Person Name'}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="register-name"
                />
                {formData.role !== 'student' && (
                  <p className="text-xs text-muted-foreground">The primary contact person for the account</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="register-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+44 234 567 8900"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="register-phone"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password (min 6 characters)"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  data-testid="register-confirm-password"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-lime-500 hover:bg-lime-600 text-white"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? 'Creating Account...' : hasSelectedPackage ? 'Create Account & Continue' : 'Create Account'}
              </Button>
            </form>
            
            {!hasSelectedPackage && (
              <div className="mt-4 p-3 rounded-lg bg-muted/50 text-center">
                <p className="text-sm text-muted-foreground">
                  Want to choose a plan first?{' '}
                  <Link to="/pricing" className="text-lime-500 hover:underline font-medium">
                    View Pricing
                  </Link>
                </p>
              </div>
            )}
            
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to="/login" className="text-lime-500 hover:underline font-medium">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
